import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export function BulkCompanies({ open, onOpenChange, projects, onDone }) {
  const [comps, setComps] = useState([]);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/companies").then((r) => setComps(r.data));
    setSel(Object.fromEntries(projects.map((p) => [p.id, p.company_ids || []])));
  }, [open, projects]);
  const toggle = (pid, cid) => setSel((s) => ({ ...s, [pid]: s[pid].includes(cid) ? s[pid].filter((x) => x !== cid) : [...s[pid], cid] }));
  const changed = projects.filter((p) => sel[p.id] && sel[p.id].length && JSON.stringify([...sel[p.id]].sort()) !== JSON.stringify([...(p.company_ids || [])].sort()));
  const save = async () => {
    setBusy(true);
    let ok = 0;
    for (const p of changed) {
      try { await api.put(`/projects/${p.id}/companies`, { company_ids: sel[p.id] }); ok += 1; } catch (e) { toast.error(`${p.code}: ${errMsg(e)}`); }
    }
    setBusy(false);
    toast.success(`${ok} project${ok === 1 ? "" : "s"} updated`);
    onDone();
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="bulk-companies-dialog">
        <DialogHeader><DialogTitle>Assign companies</DialogTitle><DialogDescription>Tick every company each project belongs to. Projects left with nothing ticked stay as they are.</DialogDescription></DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-slate-500"><th className="py-2 pr-3">Project</th>{comps.map((c) => <th key={c.id} className="px-2 py-2 text-center">{c.name}</th>)}</tr></thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 pr-3"><span className="font-medium">{p.code}</span> {p.name}</td>
                  {comps.map((c) => <td key={c.id} className="px-2 py-2 text-center"><input type="checkbox" className="h-4 w-4 accent-indigo-600" data-testid={`bulk-${p.code}-${c.id}`} checked={(sel[p.id] || []).includes(c.id)} onChange={() => toggle(p.id, c.id)} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-companies-save" disabled={busy || !changed.length} onClick={save}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save {changed.length || ""} change{changed.length === 1 ? "" : "s"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
